import { FormGroup, Input } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import env from 'env'
import { useFormik } from 'formik'
import { DealType, LogisticRequest } from 'modules/domain/logisticsKR/types'
import { endpoints } from 'modules/endpoints'
import { markEventAsRead } from 'modules/socket/managers'
import { replaceErrorsDotNotation } from 'modules/utils/helpers'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from 'views/components/DatePicker/DatePicker'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { SharedModalContent, SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { DraftTripsInitial } from 'views/pages/LogisticsKR/LogisticRequests/RequestsBoard/OpenRequestsBoardColumns/DraftTrips/types'
import { LogisticsKRTrip } from 'views/pages/LogisticsKR/types'
import { ShortFormControl } from 'views/styled/common'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import { AgentControls } from './AgentControls'
import { CarrierControls } from './CarrierControls'
import { getTripsReconciliationRequestValues } from './helpers'
import { RadioDealType } from './RadioDealType'

type Props = {
  onClose: (arg: boolean) => void
  activeRequest: LogisticRequest | undefined
  onSuccess: (arg: boolean) => Promise<void> | void
  carId?: string
  trailerId?: string | number
  currTrailerId?: string | number
  driverId?: string | number
  carrier?: LogisticsKRTrip['carrier']
  agent?: LogisticsKRTrip['agent']
  carrierVatValue?: number | null
  agentHasVat?: boolean
  agentCommission?: number
  tripOfferId?: string
  quantityLogistics?: number
  //Скорее всего временный пропс,
  // тк Егор попросил не дизейблить поля в модалке из откликов(поработают и видно будет)
  isCancelControlDisable?: boolean
}

export const AddDraftTripModal: FC<Props> = ({
  onClose,
  activeRequest,
  onSuccess,
  carrier,
  agent,
  carId,
  currTrailerId,
  trailerId,
  driverId,
  carrierVatValue,
  agentHasVat,
  tripOfferId,
  agentCommission,
  quantityLogistics,
  isCancelControlDisable,
}) => {
  const { t } = useTranslation('logisticBids')
  const { formProgress, formHandler } = useAFormHandler('carToTrip', { id: activeRequest?.id })
  const currency = env.REACT_APP_CURRENCY_SYMBOL

  let controlsDisable: Record<string, boolean> | undefined = {
    agent: !!agent?.id,
    carrier: !!carrier?.id,
    car: !!carId,
  }

  if (isCancelControlDisable) controlsDisable = undefined

  const formik = useFormik<DraftTripsInitial>({
    initialValues: {
      carrier: carrier?.id ?? '',
      carrier_phone: carrier?.phone ?? '',
      vat_value: carrierVatValue?.toString() ?? '0', // form control always works with string
      need_contract: false,
      car: carId ?? '',
      car_brand_model: '',
      car_licence_number: '',
      quantity_logistics: quantityLogistics,
      trailer: currTrailerId ?? trailerId ?? '',
      trailer_brand_model: '',
      trailer_licence_number: '',
      without_trailer: false,
      request: activeRequest?.id,
      driver: driverId ? String(driverId) : '',
      agent: '',
      price_logistics: '',
      load_date: '',
      deal_type: DealType.direct,
      has_agent_vat: agentHasVat ?? false,
      agent_commission: agentCommission ?? 0,
      trip_offer: tripOfferId,
      is_driver_a_carrier: false,
    },

    onSubmit: formHandler(
      async () => {
        await apiClient.post(endpoints.LogisticsKR.trips(), getTripsReconciliationRequestValues(formik.values))
        tripOfferId && (await markEventAsRead([{ model: 'tripoffer', object_id: tripOfferId }]))
      },
      {
        onSuccess: async () => {
          await onSuccess(true)
          onClose(false)
        },

        onError: (error) => {
          const { errors } = error
          formik.setErrors(replaceErrorsDotNotation(errors))
        },
      },
    ),
  })

  const isMediator =
    formik.values.deal_type === DealType.agent || formik.values.deal_type === DealType.forwarding_company

  return (
    <ASharedModal onClose={() => onClose(false)} id={AnalyticsPlaces.REQUESTS_BOARD.CAR_TO_TRIP_RECONCILIATION_MODAL}>
      <SharedModalTitle>{t('form.titles.add_trips_reconciliation')}</SharedModalTitle>
      <SharedModalContent>
        <SharedModalInputs>
          <FormGroup label={t('form.labels.from')}>
            <Input value={activeRequest?.loading_warehouse.address} disabled />
          </FormGroup>
          <FormGroup label={t('form.labels.to')}>
            <Input value={activeRequest?.unloading_warehouse.address} disabled />
          </FormGroup>
          <RadioDealType formik={formik} />
          {isMediator && <AgentControls formik={formik} controlsDisable={controlsDisable} />}
          <CarrierControls formik={formik} controlsDisable={controlsDisable} />
          <HrWithHeader text={t('form.block_titles.common_info')} />
          <ShortFormControl>
            <FormGroup label={t('form.labels.loading_date')} error={formik.errors.load_date} required>
              <DatePicker
                onChange={(data) => formik.setFieldValue('load_date', data['date'])}
                error={!!formik.errors.load_date}
                placeholder={t('common:datePlaceholder')}
                allowClear={true}
              />
            </FormGroup>
            <FormGroup
              label={t('form.labels.logistic_price', { currency })}
              error={formik.errors.price_logistics}
              required
            >
              <Input {...formik.getFieldProps('price_logistics')} invalid={!!formik.errors.price_logistics} />
            </FormGroup>
            <FormGroup label={t('form.labels.quantity_logistics')} error={formik.errors.quantity_logistics}>
              <Input {...formik.getFieldProps('quantity_logistics')} invalid={!!formik.errors.quantity_logistics} />
            </FormGroup>
          </ShortFormControl>
        </SharedModalInputs>
      </SharedModalContent>
      <ADefaultModalFooter
        progress={formProgress}
        onClose={() => onClose(false)}
        onConfirm={() => formik.submitForm()}
        confirmButtonText={t('common:add')}
      />
    </ASharedModal>
  )
}
